import i18n from "@/plugins/i18n";
import { DataOptions } from "vuetify";
import { defineModule } from "direct-vuex";
import { FILTER_NAMES } from "@/static/filterNames";
import { filterCapitalize } from "@/filters/capitalize";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  addContractorMapper,
  addContractorMappers,
  addContractorMappersFromSuggestions,
  editContractorMapper,
  getContractorAttributePrototypes,
  getContractorMappers,
  getContractorMapperSuggestions,
  removeContractorMapper,
} from "@/requests/administration/contractors/contractorAttributeMapping";
import {
  AttributePrototype,
  AttributePrototypesResponse,
} from "@/models/api/administration/servicesAndAttributes/AttributePrototype";
import _ from "lodash";
import {
  AttributePrototypeMapper,
  AttributePrototypeMapperResponse,
  MapperSuggestion,
} from "@/models/api/administration/servicesAndAttributes/AttributePrototypeMapper";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";

export interface AdministrationContractorAttributeMappingInterface {
  attributes: Array<AttributePrototype>;
  attributeFilters: Array<CriterionItemRequest> | null;
  attributeCount: number;
  attributeOptions: DataOptions;
  selectedAttribute: number | null;

  isMapperDialog: boolean;
  mapperCount: number | null;
  mapperOptions: DataOptions;
  mapperFilters: Array<CriterionItemRequest> | null;
  mapperItems: Array<AttributePrototypeMapper>;
  mapperSelectedItems: Array<AttributePrototypeMapper>;
  mapperSuggestions: Array<MapperSuggestion>;
  mapperAddModel: MapperSuggestion;

  searchOptions: DataOptions;
  searchAttributes: Array<AttributePrototype>;
  searchAttributesCount: number | null;
  searchSelectedAttribute: null | number;
  searchFilters: Array<CriterionItemRequest> | null;
}

export const AdministrationContractorAttributeMappingState: AdministrationContractorAttributeMappingInterface = {
  attributes: [],
  attributeFilters: null,
  attributeCount: -1,
  attributeOptions: new DefaultDataOptions(),
  selectedAttribute: null,

  isMapperDialog: false,
  mapperCount: null,
  mapperItems: [],
  mapperOptions: new DefaultDataOptions(),
  mapperFilters: null,
  mapperSelectedItems: [],
  mapperSuggestions: [],
  mapperAddModel: new MapperSuggestion(),

  searchOptions: new DefaultDataOptions(),
  searchAttributes: [],
  searchAttributesCount: null,
  searchSelectedAttribute: null,
  searchFilters: null,
};

const AdministrationContractorAttributeMappingModule = defineModule({
  state: AdministrationContractorAttributeMappingState as AdministrationContractorAttributeMappingInterface,
  namespaced: true as const,
  getters: {
    selectedAttributeName(...args): string {
      const { state } = _moduleGetterContext(args);
      return state.attributes.find((v) => v.id === state.selectedAttribute)?.defaultLabel as string;
    },
  },
  mutations: {
    setAttributeFilters(state, param: Array<CriterionItemRequest> | null) {
      state.attributeFilters = param;
    },
    setAttributes(state, param: AttributePrototypesResponse) {
      state.attributes = param.items;
      state.attributeCount = param.totalCount;
    },
    setAttributeOptions(state, param: DataOptions) {
      state.attributeOptions = param;
    },
    setSelectedAttribute(state, param: number | null) {
      state.selectedAttribute = param;
    },

    setIsMapperDialog(state, param: boolean) {
      state.isMapperDialog = param;
    },
    setMapperItems(state, param: AttributePrototypeMapperResponse) {
      state.mapperCount = param.totalCount;
      state.mapperItems = param.items;
    },
    setMapperOptions(state, param: DataOptions) {
      state.mapperOptions = param;
    },
    setMapperFilters(state, param: Array<CriterionItemRequest> | null) {
      state.mapperFilters = param;
    },
    setMapperSelectedItems(state, param: Array<AttributePrototypeMapper>) {
      state.mapperSelectedItems = param;
    },
    setMapperSuggestions(state, param: Array<MapperSuggestion>) {
      state.mapperSuggestions = param;
    },
    setMapperAddModel(state, param: MapperSuggestion) {
      state.mapperAddModel = param;
    },

    setSearchFilters(state, param: Array<CriterionItemRequest> | null) {
      state.searchFilters = param;
    },
    setSearchSelectedAttribute(state, param: number | null) {
      state.searchSelectedAttribute = param;
    },
    setSearchOptions(state, param: DataOptions) {
      state.searchOptions = param;
    },
    setSearchAttributes(state, param: AttributePrototypesResponse) {
      state.searchAttributes = param.items;
      state.searchAttributesCount = param.totalCount;
    },
  },
  actions: {
    async downloadAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      const response = await getContractorAttributePrototypes({
        count: state.attributeOptions.itemsPerPage,
        page: state.attributeOptions.page - 1,
        criterions: _.cloneDeep(state.attributeFilters),
        filterName: FILTER_NAMES.AttributePrototypeListFilter,
      });
      commit.setAttributes(response);
    },
    async downloadSearchAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      const attributes = await getContractorAttributePrototypes({
        count: state.searchOptions.itemsPerPage,
        page: state.searchOptions.page - 1,
        criterions: _.cloneDeep(state.searchFilters),
        filterName: FILTER_NAMES.AttributePrototypeListFilter,
      });
      commit.setSearchAttributes(attributes);
    },
    async openMapperDialog(context, attributePrototypeId: number) {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setSelectedAttribute(attributePrototypeId);
      await dispatch.downloadMapperItems();
      commit.setIsMapperDialog(true);
      commit.setMapperAddModel(new MapperSuggestion());
    },

    async downloadMapperItems(context) {
      const { rootState, state, commit } = _moduleActionContext(context);
      if (state.selectedAttribute && rootState.administration.contractors.list.selectedContractorId) {
        const mapperItems = await getContractorMappers(
          rootState.administration.contractors.list.selectedContractorId,
          state.selectedAttribute,
          {
            count: state.mapperOptions.itemsPerPage,
            page: state.mapperOptions.page - 1,
            criterions: _.cloneDeep(state.mapperFilters),
            filterName: FILTER_NAMES.AttributeMapperFilter,
          }
        );
        commit.setMapperItems(mapperItems);
      }
    },
    async downloadMapperSuggestions(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedAttribute !== null) {
        const mapperItems = await getContractorMapperSuggestions(state.selectedAttribute);
        commit.setMapperSuggestions(mapperItems);
      }
    },
    async addMappingFromSuggestions(context) {
      const { rootState, state, dispatch } = _moduleActionContext(context);
      const suggestions = state.mapperSuggestions.filter((x) => x.newValue);
      if (
        suggestions.length &&
        state.selectedAttribute &&
        rootState.administration.contractors.list.selectedContractorId
      ) {
        await addContractorMappersFromSuggestions(
          rootState.administration.contractors.list.selectedContractorId,
          state.selectedAttribute,
          suggestions
        );
        await dispatch.downloadMapperSuggestions();
      }
    },
    async addMappingItem(context) {
      const { rootState, state, rootDispatch, dispatch, commit } = _moduleActionContext(context);
      if (state.selectedAttribute && rootState.administration.contractors.list.selectedContractorId) {
        await rootDispatch.failIfRequestsInProgress();
        await addContractorMapper(
          rootState.administration.contractors.list.selectedContractorId,
          state.selectedAttribute,
          state.mapperAddModel
        );
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
        commit.setMapperAddModel(new MapperSuggestion());
        await dispatch.downloadMapperItems();
      }
    },
    async editMappingItem(context, attributePrototypeMapper: AttributePrototypeMapper) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedAttribute !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await editContractorMapper(
          state.selectedAttribute,
          {
            newValue: attributePrototypeMapper.newValue,
            oldValue: attributePrototypeMapper.oldValue,
          },
          attributePrototypeMapper.id
        );
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async removeMappingItem(context, mappingId: number) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.selectedAttribute !== null) {
        if (
          state.mapperItems.find((v) => v.id === mappingId)?.usedInAttributes.length === 0 ||
          (await rootDispatch.popup.addConfirmRemoveElement())
        ) {
          await removeContractorMapper(state.selectedAttribute, mappingId);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          await dispatch.downloadMapperItems();
        }
      }
    },
    async copyAttributeMappers(context) {
      const { rootState, state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.searchSelectedAttribute && rootState.administration.contractors.list.selectedContractorId) {
        await rootDispatch.failIfRequestsInProgress();
        await addContractorMappers(
          rootState.administration.contractors.list.selectedContractorId,
          state.searchSelectedAttribute,
          state.mapperSelectedItems.map((x) => x.id)
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadMapperItems();
      } else {
        rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("selectAnAttributeFirst")), "error"));
        throw new Error("Attribute not selected");
      }
    },
  },
});

export default AdministrationContractorAttributeMappingModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorAttributeMappingModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorAttributeMappingModule);
